import { useState } from "react";
import { Link } from "react-router-dom";
import { mockProducts, mockCategories } from "@/mocks/products";

const FACTORY_COMMISSION: Record<string, number> = {
  "TechVision Factory": 10,
  "PureGlow Labs": 15,
  "FitForge Manufacturing": 8,
  "GreenThread Mills": 12,
  "LightCraft Industries": 9,
  "HydroForge Co.": 7,
  "SoundWave Factory": 11,
  "EcoKitchen Works": 6,
};

const FACTORY_ID: Record<string, string> = {
  "TechVision Factory": "techvision",
  "PureGlow Labs": "pureglow",
  "FitForge Manufacturing": "fitforge",
};

const FACTORY_LOCATION: Record<string, string> = {
  "TechVision Factory": "Austin, TX",
  "PureGlow Labs": "Los Angeles, CA",
  "FitForge Manufacturing": "Denver, CO",
  "GreenThread Mills": "Portland, OR",
  "LightCraft Industries": "Dallas, TX",
  "HydroForge Co.": "Phoenix, AZ",
  "SoundWave Factory": "Nashville, TN",
  "EcoKitchen Works": "Seattle, WA",
};

const SOLD_COUNTS: Record<string, string> = {
  "1": "12.4k sold",
  "2": "8.9k sold",
  "3": "5.2k sold",
  "4": "21.3k sold",
  "5": "9.7k sold",
  "6": "7.1k sold",
  "7": "3.4k sold",
  "8": "18.6k sold",
};

export default function FeaturedProducts() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [wishlist, setWishlist] = useState<string[]>([]);

  const filtered = activeCategory === "all"
    ? mockProducts
    : mockProducts.filter((p) => p.category.toLowerCase() === activeCategory);

  const toggleWishlist = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWishlist((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  return (
    <section className="py-20 px-4 md:px-8" style={{ backgroundColor: "#f5f5f5" }}>
      <div className="max-w-7xl mx-auto">

        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide">
                <i className="ri-fire-fill" /> Hot Right Now
              </span>
              <span className="flex items-center gap-1.5 bg-green-50 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-green-100">
                <i className="ri-map-pin-2-fill" /> USA Local Factories
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight">
              Popular Products from<br />
              <span className="text-orange-500">USA Local Verified Factory</span>
            </h2>
            <p className="text-gray-500 text-sm mt-1.5">
              Sourced directly from <strong className="text-gray-700">verified American factories</strong> — no middlemen, factory-direct pricing
            </p>
          </div>
          <Link
            to="/marketplace"
            className="flex items-center gap-2 px-5 py-2.5 border-2 border-orange-400 text-orange-500 font-bold text-sm rounded-full hover:bg-orange-500 hover:text-white transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
          >
            Browse All Products <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Trust Bar */}
        <div className="flex flex-wrap items-center gap-6 mb-8 px-5 py-4 bg-white rounded-2xl border border-gray-100">
          {[
            { icon: "ri-shield-check-fill", text: "All factories verified & certified", color: "text-green-500" },
            { icon: "ri-map-pin-2-fill", text: "Ships from USA warehouses", color: "text-orange-500" },
            { icon: "ri-truck-line", text: "2–5 day domestic delivery", color: "text-orange-500" },
            { icon: "ri-money-dollar-circle-line", text: "Earn up to 15% reseller commission", color: "text-amber-500" },
            { icon: "ri-refresh-line", text: "30-day easy returns", color: "text-green-500" },
          ].map((t) => (
            <div key={t.text} className="flex items-center gap-2 text-xs text-gray-600 whitespace-nowrap">
              <i className={`${t.icon} ${t.color} text-base`} />
              {t.text}
            </div>
          ))}
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {mockCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? "bg-orange-500 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-500"
              }`}
            >
              <i className={cat.icon} />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
          {filtered.map((product, index) => {
            const commission = FACTORY_COMMISSION[product.seller] || 0;
            const factoryId = FACTORY_ID[product.seller];
            const location = FACTORY_LOCATION[product.seller] || "USA";
            const soldCount = SOLD_COUNTS[product.id] || "1k+ sold";
            const isHot = index < 3;
            const discount = Math.round((1 - product.price / product.originalPrice) * 100);

            return (
              <div
                key={product.id}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-orange-200 transition-all group cursor-pointer flex flex-col"
              >
                {/* Image */}
                <div className="relative overflow-hidden" style={{ aspectRatio: "1/1" }}>
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />

                  {/* Top Left Badges */}
                  <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
                    {isHot && (
                      <span className="flex items-center gap-1 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        <i className="ri-fire-fill text-xs" /> HOT
                      </span>
                    )}
                    {product.verified && (
                      <span className="flex items-center gap-1 bg-white/95 text-green-600 text-xs font-semibold px-2 py-1 rounded-full">
                        <i className="ri-shield-check-fill text-xs" /> Verified
                      </span>
                    )}
                    {product.badge && !isHot && (
                      <span className="bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                        {product.badge}
                      </span>
                    )}
                  </div>

                  {/* Discount Badge */}
                  <div className="absolute top-2.5 right-10 bg-red-500 text-white text-xs font-extrabold px-2 py-1 rounded-full">
                    -{discount}%
                  </div>

                  {/* Wishlist */}
                  <button
                    onClick={(e) => toggleWishlist(product.id, e)}
                    className="absolute top-2.5 right-2.5 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full cursor-pointer hover:bg-white transition-colors"
                  >
                    <i className={wishlist.includes(product.id) ? "ri-heart-fill text-red-500" : "ri-heart-line text-gray-400"} />
                  </button>

                  {/* Commission Overlay */}
                  {commission > 0 && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-white text-xs font-bold">
                          <i className="ri-money-dollar-circle-line mr-1" />{commission}% reseller commission
                        </span>
                        <span className="text-white/80 text-xs">${(product.price * commission / 100).toFixed(2)}/sale</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4 flex flex-col flex-1">
                  {/* Factory Info */}
                  <div className="flex items-center gap-1.5 mb-2">
                    <i className="ri-building-line text-gray-400 text-xs" />
                    {factoryId ? (
                      <Link
                        to={`/factory/${factoryId}`}
                        className="text-xs text-orange-500 hover:underline font-medium cursor-pointer truncate"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {product.seller}
                      </Link>
                    ) : (
                      <span className="text-xs text-gray-500 truncate">{product.seller}</span>
                    )}
                    <span className="text-gray-300 text-xs">·</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">🇺🇸 {location}</span>
                  </div>

                  <h3 className="text-sm font-bold text-gray-900 line-clamp-2 mb-2 leading-snug flex-1">
                    {product.title}
                  </h3>

                  {/* Rating + Sold */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-0.5">
                      {[1,2,3,4,5].map((star) => (
                        <i
                          key={star}
                          className={`text-xs ${star <= Math.floor(product.rating) ? "ri-star-fill text-yellow-400" : "ri-star-line text-gray-300"}`}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">{product.rating}</span>
                    <span className="text-gray-300 text-xs">·</span>
                    <span className="text-xs text-gray-500 font-medium">{soldCount}</span>
                  </div>

                  {/* Price Row */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl font-extrabold text-gray-900">${product.price}</span>
                    <span className="text-sm text-gray-400 line-through">${product.originalPrice}</span>
                  </div>

                  {/* Stock indicator */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-400">Stock</span>
                      <span className="text-orange-500 font-semibold">
                        {product.stock > 1000 ? "In Stock" : `Only ${product.stock} left`}
                      </span>
                    </div>
                    <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-400 rounded-full"
                        style={{ width: `${Math.min(100, (product.stock / 5000) * 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex gap-2">
                    <Link
                      to="/cart"
                      className="flex-1 py-2.5 bg-orange-500 text-white text-xs font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className="ri-shopping-cart-line" /> Add to Cart
                    </Link>
                    {factoryId && (
                      <Link
                        to={`/factory/${factoryId}`}
                        className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-xl text-gray-500 hover:border-orange-300 hover:text-orange-500 transition-colors cursor-pointer flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                        title="View Factory"
                      >
                        <i className="ri-store-line text-sm" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 rounded-2xl overflow-hidden relative">
          <img
            src="https://public.readdy.ai/ai/img_res/d8ce69d5cc26e040925eb76c53984ab9.jpg"
            alt="USA Factories"
            className="w-full h-40 md:h-52 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-transparent flex items-center px-8 md:px-14">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 w-full">
              <div className="flex-1">
                <h3 className="text-xl md:text-2xl font-extrabold text-white mb-1">
                  500+ Verified USA Factories
                </h3>
                <p className="text-white/70 text-sm">
                  All factories are background-checked, certified, and ship from US warehouses
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <Link
                  to="/marketplace"
                  className="px-6 py-3 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Shop All Products
                </Link>
                <Link
                  to="/wholesale"
                  className="px-6 py-3 bg-white/15 backdrop-blur-sm border border-white/30 text-white text-sm font-semibold rounded-full hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Wholesale B2B
                </Link>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
